import { ReactElement, SVGProps, useState } from 'react';

import {
  IconBuilding,
  IconClipboard,
  IconDots,
  IconFileText,
  IconGearOutline,
  IconHelpingHand,
  IconMultipleUsersOutline,
  IconPackageOpen,
  IconPresentationOutline,
  IconRefreshCcw,
} from '@iwoca/icons';
import { TextArea } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import styles from './PurposeScreen.module.css';
import { useLocale } from '../../../../hooks/useLocale';
import ButtonLink from '../../../ButtonLink/ButtonLink';
import { LOAN_PURPOSE_OPTIONS_DE } from '../../../GetStarted/GetStartedDE/loanOptions';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { OptionCards, TOption } from '../../OptionCards/OptionCards';
import { StepLayout } from '../../StepLayout/StepLayout';

type TPurpose = (typeof LOAN_PURPOSE_OPTIONS_DE)[number]['value'];

type TIcon = (props: SVGProps<SVGSVGElement>) => ReactElement;

const PURPOSE_ICONS: Record<TPurpose, TIcon> = {
  stock_purchase: IconPackageOpen,
  bridging_loan: IconHelpingHand,
  pay_bill: IconFileText,
  equipment_purchase: IconGearOutline,
  financing_client_order: IconClipboard,
  pay_tax_bill: IconBuilding,
  refinancing_debt: IconRefreshCcw,
  pay_staff: IconMultipleUsersOutline,
  marketing: IconPresentationOutline,
  other: IconDots,
};

// DIT-678. One step, two states: card picker → (if "other") a textarea for the
// detail. Kept as internal state, not a separate registry screen, so the step
// count stays stable. Reads/writes the shared mobile signup form store
// (DIT-664). `showDetail` stays local — it's view state, not form data.
export const PurposeScreen = () => {
  const { t } = useLingui();
  const { i18n } = useLocale();
  const { register, watch, setValue } = useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();
  const { fieldProps } = useStageFieldTracking();
  const [showDetail, setShowDetail] = useState(
    () => watch('loanPurpose') === 'other',
  );

  const loanPurpose = watch('loanPurpose');
  const loanPurposeDetails = watch('loanPurposeDetails');

  const options: TOption<TPurpose>[] = LOAN_PURPOSE_OPTIONS_DE.map((option) => {
    const Icon = PURPOSE_ICONS[option.value];
    return {
      value: option.value,
      icon: <Icon width={24} height={24} aria-hidden />,
      label: i18n._(option.label),
    };
  });

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`What do you want to use the loan for?`}
      hideCta={!showDetail}
      ctaDisabled={!loanPurposeDetails?.trim()}
      onContinue={goNext}
      fieldProps={fieldProps}
    >
      {showDetail ? (
        <>
          <ButtonLink
            type="button"
            className={styles.backToOptions}
            onClick={() => {
              // Clear both so a later pick doesn't submit stale detail and a
              // remount doesn't re-derive showDetail back into this screen.
              setValue('loanPurpose', '');
              setValue('loanPurposeDetails', '');
              setShowDetail(false);
            }}
          >
            {'← '}
            {t`Back`}
          </ButtonLink>
          <TextArea
            {...register('loanPurposeDetails')}
            rows={4}
            className={styles.detail}
            label={t`Additional Information`}
            placeholder={t`Please enter a purpose`}
          />
        </>
      ) : (
        <OptionCards
          name="loanPurpose"
          options={options}
          value={(loanPurpose as TPurpose) || undefined}
          onChange={(value) => {
            setValue('loanPurpose', value, { shouldDirty: true });
            if (value === 'other') {
              setShowDetail(true);
            } else {
              goNext();
            }
          }}
        />
      )}
    </StepLayout>
  );
};
